import { useMutation } from '@apollo/client';
import type { InsertInternalAuditTestResultMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { InsertInternalAuditTestResultDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import type { InsertControlTestInternalAuditResultInput } from './useInsertControlTestInternalAuditResultTRPC';
import { useInsertControlTestInternalAuditResultTRPC } from './useInsertControlTestInternalAuditResultTRPC';

export const useInsertControlTestInternalAuditResult = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  const [insertGraphQL, graphqlState] = useMutation(
    InsertInternalAuditTestResultDocument,
    {
      update: (cache) => {
        evictField(cache, 'control_test_internal_audit_result');
        evictField(cache, 'internal_audit_report');
      },
    }
  );

  const trpcMutation = useInsertControlTestInternalAuditResultTRPC();

  const insertControlTestInternalAuditResult = async (
    variables: InsertControlTestInternalAuditResultInput
  ): Promise<InsertInternalAuditTestResultMutation> => {
    if (trpcEnabled) {
      return trpcMutation.insertControlTestInternalAuditResult(variables);
    }

    const result = await insertGraphQL({ variables });
    if (!result.data) {
      throw new Error('Failed to insert control test internal audit result');
    }

    return result.data;
  };

  if (trpcEnabled) {
    return {
      insertControlTestInternalAuditResult,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    insertControlTestInternalAuditResult,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
